// src/PatientPortalHub/PatientFamilyHub.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

import {
  collectionGroup,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
  addDoc,
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, listAll } from "firebase/storage";
import { ArrowLeft, Users, HeartHandshake } from "lucide-react";

import { db, storage, auth } from "../firebase";
import { makeDoctorAvatar } from "../utils/images";
import PostCard from "../components/patient/Home/PostCard";

/** small helper */
const toJsDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

/** 🔹 Simple in-memory cache so we don't call getDownloadURL for the same path 100x */
const mediaUrlCache = {};

/** 🔹 Resolve Storage paths → HTTPS URLs (keep plain URLs as-is) */
const resolveMediaFiles = async (rawFiles = []) => {
  if (!Array.isArray(rawFiles) || rawFiles.length === 0) return [];

  const results = await Promise.all(
    rawFiles.map(async (f) => {
      if (!f || typeof f !== "string") return null;

      // Already a full URL → keep it
      if (f.startsWith("http://") || f.startsWith("https://")) {
        return f;
      }

      // Storage path → try to resolve
      if (mediaUrlCache[f]) return mediaUrlCache[f];

      try {
        const fileRef = ref(storage, f);
        const url = await getDownloadURL(fileRef);
        mediaUrlCache[f] = url;
        return url;
      } catch (err) {
        console.error("Failed to resolve storage media path:", f, err);
        return null;
      }
    })
  );

  return results.filter(Boolean);
};

const PatientFamilyHub = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedPosts, setExpandedPosts] = useState({});
  const [viewedPosts, setViewedPosts] = useState({});
  const [inViewPosts, setInViewPosts] = useState({}); // 👀 track if each post is in view

  const [postLikes, setPostLikes] = useState({});
  const [postComments, setPostComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [likeLoading, setLikeLoading] = useState({});
  const [followedDoctors, setFollowedDoctors] = useState({});
  const [followLoading, setFollowLoading] = useState({});
  const [visibleCount, setVisibleCount] = useState(10);

  // --------- utilities ---------

  const fetchDoctorPhoto = useCallback(async (doctorId, doctorInfo) => {
    const inline =
      doctorInfo?.photoURL ||
      doctorInfo?.photoUrl ||
      doctorInfo?.profilePicture;
    if (inline) return inline;

    try {
      const folderRef = ref(storage, `doctor_photos/${doctorId}`);
      const files = await listAll(folderRef);
      if (files.items.length > 0) {
        return await getDownloadURL(files.items[0]);
      }
    } catch {
      // ignore
    }

    return makeDoctorAvatar(doctorInfo?.name || "Doctor");
  }, []);

  const loadFollowedDoctors = useCallback(async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const followedList = snap.data().followedDoctors || [];
        const state = {};
        followedList.forEach((id) => (state[id] = true));
        setFollowedDoctors(state);
      }
    } catch (e) {
      console.error("Error loading followed doctors:", e);
    }
  }, []);

  // --------- Infinite scroll ---------
  useEffect(() => {
    const handleScroll = () => {
      if (visibleCount >= posts.length) return;

      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.body.offsetHeight - 300; // 300px from bottom

      if (scrollPosition >= threshold) {
        setVisibleCount((prev) => Math.min(prev + 10, posts.length));
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleCount, posts.length]);

  // --------- Fetch posts ---------
  useEffect(() => {
    if (!user) {
      setLoading(false);
      setPosts([]);
      return;
    }

    const fetchAllPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        const itemsSnapshot = await getDocs(collectionGroup(db, "items"));
        if (itemsSnapshot.empty) {
          setPosts([]);
          setLoading(false);
          return;
        }

        const itemDocs = itemsSnapshot.docs;
        const pairs = itemDocs
          .map((d) => ({
            item: d,
            doctorId: d.ref.path.split("/")[1], // doctors/{doctorId}/items/{itemId} OR uploads/{doctorId}/items/{itemId}
          }))
          .filter((x) => x.doctorId);

        const uniqueDoctorIds = [...new Set(pairs.map((p) => p.doctorId))];

        const doctorEntries = await Promise.all(
          uniqueDoctorIds.map(async (id) => {
            const ds = await getDoc(doc(db, "doctors", id));
            return [id, ds.exists() ? ds.data() : null];
          })
        );
        const doctorMap = new Map(doctorEntries);

        const postsData = await Promise.all(
          pairs.map(async ({ item, doctorId }) => {
            const data = item.data();
            const doctorInfo = doctorMap.get(doctorId);
            if (!doctorInfo) return null;

            const doctorPhoto = await fetchDoctorPhoto(doctorId, doctorInfo);

            // 🔹 Resolve media (images/videos) from Storage paths → URLs
            const rawFiles = Array.isArray(data.files) ? data.files : [];
            const resolvedFiles = await resolveMediaFiles(rawFiles);

            return {
              id: item.id,
              path: item.ref.path,
              ...data,
              files: resolvedFiles, // 👈 ensure PostCard sees real URLs
              doctorId,
              doctorName: doctorInfo?.name || "Unknown Doctor",
              doctorPhoto,
              specialty: doctorInfo?.specialty || "General",
              createdAt: data.createdAt?.toDate?.() || new Date(0),
              likes: data.likes || 0,
              likedBy: data.likedBy || [],
              commentCount: data.commentCount || 0,
              followerCount: doctorInfo?.followerCount || 0,
              views: data.views || 0,
            };
          })
        );

        const clean = postsData
          .filter(Boolean)
          .sort((a, b) => b.createdAt - a.createdAt);

        setPosts(clean);

        setVisibleCount((prev) => Math.min(prev || 10, clean.length || 0));

        const likesState = {};
        clean.forEach((post) => {
          likesState[post.id] = {
            count: post.likes,
            isLiked: user ? post.likedBy.includes(user.uid) : false,
          };
        });
        setPostLikes(likesState);

        await loadFollowedDoctors(user.uid);
      } catch (e) {
        console.error("Failed to fetch Education Hub posts:", e);
        setError("Nuk po arrijmë të ngarkojmë postimet.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllPosts();
  }, [user, fetchDoctorPhoto, loadFollowedDoctors]);

  // --------- follow / like / comments ---------

  const toggleFollowDoctor = useCallback(
    async (doctorId) => {
      if (!user) return alert("Duhet të hysh për të ndjekur mjekët.");
      const isFollowing = !!followedDoctors[doctorId];

      setFollowLoading((p) => ({ ...p, [doctorId]: true }));
      setFollowedDoctors((p) => ({ ...p, [doctorId]: !isFollowing }));

      try {
        const userRef = doc(db, "users", user.uid);
        const doctorRef = doc(db, "doctors", doctorId);

        if (isFollowing) {
          await updateDoc(userRef, {
            followedDoctors: arrayRemove(doctorId),
          });
          await updateDoc(doctorRef, {
            followerCount: increment(-1),
            followers: arrayRemove(user.uid),
          });
        } else {
          await updateDoc(userRef, {
            followedDoctors: arrayUnion(doctorId),
          });
          await updateDoc(doctorRef, {
            followerCount: increment(1),
            followers: arrayUnion(user.uid),
          });
          await addDoc(collection(db, `doctors/${doctorId}/notifications`), {
            type: "new_follower",
            userId: user.uid,
            userName:
              user.displayName || user.email?.split("@")[0] || "User",
            userAvatar:
              user.photoURL ||
              `https://via.placeholder.com/32/4F46E5/FFFFFF?text=U`,
            createdAt: new Date(),
            read: false,
          });
        }

        setPosts((prev) =>
          prev.map((p) =>
            p.doctorId === doctorId
              ? {
                  ...p,
                  followerCount: p.followerCount + (isFollowing ? -1 : 1),
                }
              : p
          )
        );
      } catch (e) {
        console.error("Error toggle follow:", e);
        setFollowedDoctors((p) => ({
          ...p,
          [doctorId]: isFollowing,
        }));
        alert("Nuk u përditësua statusi i ndjekjes.");
      } finally {
        setFollowLoading((p) => ({ ...p, [doctorId]: false }));
      }
    },
    [user, followedDoctors]
  );

  const toggleLike = useCallback(
    async (post) => {
      if (!user) return alert("Duhet të hysh për të pëlqyer postime.");
      const postId = post.id;
      const current = postLikes[postId] || {
        count: 0,
        isLiked: false,
      };

      setLikeLoading((p) => ({ ...p, [postId]: true }));
      setPostLikes((p) => ({
        ...p,
        [postId]: {
          count: current.isLiked
            ? current.count - 1
            : current.count + 1,
          isLiked: !current.isLiked,
        },
      }));

      try {
        const postRef = doc(db, post.path);
        if (current.isLiked) {
          await updateDoc(postRef, {
            likes: increment(-1),
            likedBy: arrayRemove(user.uid),
          });
        } else {
          await updateDoc(postRef, {
            likes: increment(1),
            likedBy: arrayUnion(user.uid),
          });
        }
      } catch (e) {
        console.error("Error toggle like:", e);
        setPostLikes((p) => ({ ...p, [postId]: current }));
        alert("Nuk u përditësua pëlqimi.");
      } finally {
        setLikeLoading((p) => ({ ...p, [postId]: false }));
      }
    },
    [user, postLikes]
  );

  const loadComments = useCallback(
    (postPath, postId) => {
      if (postComments[postId]?.data) return;

      const qComments = query(
        collection(db, `${postPath}/comments`),
        orderBy("createdAt", "desc")
      );

      const unsubscribe = onSnapshot(qComments, (snapshot) => {
        const commentsData = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: toJsDate(d.data().createdAt) || new Date(),
        }));
        setPostComments((p) => ({
          ...p,
          [postId]: { data: commentsData, unsubscribe },
        }));
      });
    },
    [postComments]
  );

  const toggleComments = useCallback(
    (post) => {
      const postId = post.id;
      setShowComments((p) => ({ ...p, [postId]: !p[postId] }));
      if (!postComments[postId]?.data) {
        loadComments(post.path, postId);
      }
    },
    [postComments, loadComments]
  );

  const addComment = useCallback(
    async (post) => {
      if (!user) return alert("Duhet të hysh për të komentuar.");
      const postId = post.id;
      const text = newComment[postId]?.trim();
      if (!text) return;

      setCommentLoading((p) => ({ ...p, [postId]: true }));
      try {
        await addDoc(collection(db, `${post.path}/comments`), {
          text,
          authorId: user.uid,
          authorName:
            user.displayName || user.email?.split("@")[0] || "User",
          authorAvatar:
            user.photoURL ||
            `https://via.placeholder.com/32/4F46E5/FFFFFF?text=U`,
          createdAt: new Date(),
          likes: 0,
          likedBy: [],
        });
        await updateDoc(doc(db, post.path), {
          commentCount: increment(1),
        });
        setNewComment((p) => ({ ...p, [postId]: "" }));
      } catch (e) {
        console.error("Error add comment:", e);
        alert("Nuk u shtua komenti.");
      } finally {
        setCommentLoading((p) => ({ ...p, [postId]: false }));
      }
    },
    [user, newComment]
  );

  const deleteComment = useCallback(
    async (post, commentId, authorId) => {
      if (!user || user.uid !== authorId) {
        return alert("Mund të fshish vetëm komentet e tua.");
      }
      try {
        await deleteDoc(doc(db, `${post.path}/comments/${commentId}`));
        await updateDoc(doc(db, post.path), {
          commentCount: increment(-1),
        });
      } catch (e) {
        console.error("Error delete comment:", e);
        alert("Nuk u fshi komenti.");
      }
    },
    [user]
  );

  // cleanup comment listeners
  useEffect(
    () => () => {
      Object.values(postComments).forEach(({ unsubscribe }) =>
        unsubscribe?.()
      );
    },
    [postComments]
  );

  const visiblePosts = useMemo(
    () => posts.slice(0, visibleCount),
    [posts, visibleCount]
  );

  // --------- Views + in-view tracking via IntersectionObserver ---------
  useEffect(() => {
    if (!visiblePosts.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const postId = entry.target.getAttribute("data-post-id");
          const postPath = entry.target.getAttribute("data-post-path");
          if (!postId) return;

          // ✅ track whether this post is in view
          setInViewPosts((prev) => ({
            ...prev,
            [postId]: entry.isIntersecting,
          }));

          // ✅ only count a view once (first time it becomes visible)
          if (entry.isIntersecting && !viewedPosts[postId]) {
            setViewedPosts((prev) => ({ ...prev, [postId]: true }));

            if (postPath) {
              updateDoc(doc(db, postPath), {
                views: increment(1),
              }).catch((err) =>
                console.error("Failed updating views:", err)
              );
            }

            setPosts((prev) =>
              prev.map((p) =>
                p.id === postId
                  ? { ...p, views: (p.views || 0) + 1 }
                  : p
              )
            );
          }
        });
      },
      { threshold: 0.4 }
    );

    visiblePosts.forEach((p) => {
      const el = document.querySelector(`[data-post-id="${p.id}"]`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [visiblePosts, viewedPosts]);

  const buildPostUrl = (post) =>
    `${window.location.origin}/research/${post.doctorId}/${post.id}`;

  const sharePost = (post) => {
    const url = buildPostUrl(post);

    if (navigator.share) {
      navigator
        .share({
          title: post.title || "MikDok postim",
          text: post.description?.slice(0, 120) || "",
          url,
        })
        .catch(() => {
          navigator.clipboard.writeText(url);
          alert("Link u kopjua!");
        });
    } else {
      navigator.clipboard.writeText(url);
      alert("Link u kopjua!");
    }
  };

  // 🔐 Guard
  if (!user) {
    return (
      <div
        style={{
          padding: 16,
          maxWidth: 480,
          margin: "0 auto",
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
          fontSize: 14,
          color: "#4B5563",
        }}
      >
        Duhet të hysh në llogarinë tënde për të parë Education Hub.
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F3F4F6",
      }}
    >
      <div
        style={{
          padding: 16,
          maxWidth: 480,
          margin: "0 auto",
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                border: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#FFFFFF",
                cursor: "pointer",
              }}
            >
              <ArrowLeft
                style={{ width: 18, height: 18, color: "#4B5563" }}
              />
            </button>

            <div
              style={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0F172A",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Users
                  style={{ width: 18, height: 18, color: "#0F766E" }}
                />
                Education Hub
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "#6B7280",
                }}
              >
                Postime nga mjekët për ty – këshilla,
                edukim dhe pyetje që mund t’i bëni.
              </span>
            </div>

            <div style={{ flex: 1 }} />
          </div>
        </header>

        {/* Posts */}
        <div>
          <div>
            {loading ? (
              <div
                style={{
                  padding: 20,
                  fontSize: 13,
                  color: "#6B7280",
                }}
              >
                Po ngarkojmë postimet…
              </div>
            ) : error ? (
              <div
                style={{
                  padding: 20,
                  fontSize: 13,
                  color: "#B91C1C",
                }}
              >
                {error}
              </div>
            ) : posts.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  fontSize: 13,
                  color: "#6B7280",
                }}
              >
                Nuk ka ende postime nga mjekët për Education Hub.
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: "#9CA3AF",
                    display: "flex",
                    justifyContent: "center",
                    gap: 4,
                  }}
                >
                  <HeartHandshake style={{ width: 14, height: 14 }} />
                  Sapo mjekët të publikojnë këshilla, do t’i shohësh këtu.
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gap: 10,
                }}
              >
                {visiblePosts.map((post) => {
                  const likes =
                    postLikes[post.id] || {
                      count: 0,
                      isLiked: false,
                    };
                  const comments = postComments[post.id]?.data || [];
                  const commentCount =
                    comments.length > 0
                      ? comments.length
                      : post.commentCount || 0;

                  return (
                    <div
                      key={post.id}
                      data-post-id={post.id}
                      data-post-path={post.path}
                      style={{ scrollMarginTop: 80 }}
                    >
                      <PostCard
                        post={post}
                        likes={likes}
                        likeLoading={!!likeLoading[post.id]}
                        isExpanded={!!expandedPosts[post.id]}
                        showComments={!!showComments[post.id]}
                        comments={comments}
                        commentCount={commentCount}
                        views={post.views || 0}
                        newComment={newComment[post.id] || ""}
                        commentLoading={!!commentLoading[post.id]}
                        isFollowing={!!followedDoctors[post.doctorId]}
                        followLoading={!!followLoading[post.doctorId]}
                        onToggleLike={() => toggleLike(post)}
                        onToggleComments={() => toggleComments(post)}
                        onToggleExpanded={(postId) =>
                          setExpandedPosts((prev) => ({
                            ...prev,
                            [postId]: !prev[postId],
                          }))
                        }
                        onChangeComment={(val) =>
                          setNewComment((p) => ({
                            ...p,
                            [post.id]: val,
                          }))
                        }
                        onAddComment={() => addComment(post)}
                        onDeleteComment={(cid, aid) =>
                          deleteComment(post, cid, aid)
                        }
                        onCommentKeyPress={(e) =>
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          addComment(post)
                        }
                        onFollowDoctor={() =>
                          toggleFollowDoctor(post.doctorId)
                        }
                        onDoctorClick={() =>
                          navigate(`/doctor/${post.doctorId}`)
                        }
                        onShare={() => sharePost(post)}
                        // 👇 Card knows if it's in viewport (for later UX if you need)
                        isInView={!!inViewPosts[post.id]}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Static info bottom */}
        <div
          style={{
            marginTop: 16,
            borderRadius: 16,
            padding: "12px 14px",
            background:
              "linear-gradient(135deg, rgba(59,130,246,0.06), rgba(45,212,191,0.06))",
            border: "1px solid rgba(148,163,184,0.3)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
            }}
          >
            Mbaje veten të informuar 🌱
          </span>
          <span
            style={{
              fontSize: 12,
              color: "#4B5563",
            }}
          >
            Lexoni këto postime – për pyetje para vizitës,
            kujdes pas terapisë dhe zakone të shëndetshme.
          </span>
        </div>
      </div>
    </div>
  );
};

export default PatientFamilyHub;
